import React, { useState, useEffect, useContext, useRef, useCallback } from "react";
import NavigationBar from "../components/Navigation";
import Slider from "../components/Slider";
import Footer from "../components/Footer";
import SectionsAccueil from "../components/sectionsAccueil";
import ActeurContext from "../contexts/ActeurContext";
import { useNavigate, useLocation } from "react-router-dom";
import BackToTopButton from '../components/BackToTopButton';
import { Helmet } from "react-helmet-async";
import { UserContext } from "../UserContext";
import EmuleProfil from "../components/EmuleProfil";
import ChatWindow from "../components/ChatWindow";
import { db } from "../config/firebasedeconfig";
import { doc, getDoc } from "firebase/firestore";
import Modal from 'react-bootstrap/Modal';
import "../styles/components/Accueil.scss";

const Accueil = () => {
  const cinematicBridgeRef = useRef(null);

  useEffect(() => {
    const bridge = cinematicBridgeRef.current;
    if (!bridge) return;

    let frameId = null;

    const updateBridge = () => {
      frameId = null;

      const rect = bridge.getBoundingClientRect();
      const center = rect.top + rect.height / 2;
      const viewport = window.innerHeight;

      // Début à 80 % de la hauteur de l'écran,
      // fin à 35 % : la barre reste visible pendant l'expansion.
      const start = viewport * 0.80;
      const end = viewport * 0.35;

      const progress = Math.max(
        0,
        Math.min(1, (start - center) / (start - end))
      );

      const scale = 0.01 + progress * 0.99;

      bridge.style.setProperty("--mf-bridge-scale", scale);
    };

    const scheduleUpdate = () => {
      if (frameId === null) {
        frameId = requestAnimationFrame(updateBridge);
      }
    };

    window.addEventListener("scroll", scheduleUpdate, { passive: true });
    window.addEventListener("resize", scheduleUpdate);

    scheduleUpdate();

    return () => {
      window.removeEventListener("scroll", scheduleUpdate);
      window.removeEventListener("resize", scheduleUpdate);

      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }
    };
  }, []);
  const history = useNavigate();
  const location = useLocation();
  const { userId } = useContext(UserContext);
  const [showEmuleProfil, setShowEmuleProfil] = useState(false);
  const [emuleUser, setEmuleUser] = useState(null);
  const [showChatWindow, setShowChatWindow] = useState(false);
  const [chatPeer, setChatPeer] = useState(null);

  const scrollToSection = useCallback((selector, attempts = 40, delay = 250) => {
    if (!selector) return;

    const scrollElementWithOffset = () => {
      const target = document.querySelector(selector);
      if (!target) return false;

      const nav = document.querySelector('.navigation');
      const navHeight = nav ? nav.getBoundingClientRect().height : 0;
      const extraOffset = window.innerWidth <= 992 ? 150 : 60;
      const offset = navHeight + extraOffset;
      const targetTop = target.getBoundingClientRect().top + window.pageYOffset - offset;

      window.scrollTo({
        top: Math.max(targetTop, 0),
        behavior: "smooth",
      });

      const followUps = window.innerWidth <= 992 ? 3 : 1;
      for (let i = 1; i <= followUps; i++) {
        setTimeout(() => {
          const element = document.querySelector(selector);
          if (!element) return;
          const updatedTop = element.getBoundingClientRect().top + window.pageYOffset - offset;
          window.scrollTo({
            top: Math.max(updatedTop, 0),
            behavior: "smooth",
          });
        }, i * 500);
      }

      return true;
    };

    if (scrollElementWithOffset()) {
      return;
    }

    if (attempts > 0) {
      setTimeout(() => scrollToSection(selector, attempts - 1, delay), delay);
    }
  }, []);

  // Référence pour éviter les ouvertures multiples
  const lastRequestTimeRef = useRef(0);

  // Fonction pour ouvrir le profil d'un utilisateur
  const openEmuleProfil = async (data) => {
    // Vérifier si l'événement n'est pas trop rapproché d'un précédent (éviter les doublons)
    const now = new Date().getTime();
    if (now - lastRequestTimeRef.current < 500) return; // Ignorer les clics trop rapprochés (500ms)

    lastRequestTimeRef.current = now;

    if (data && data.userId) {
      console.log("Ouverture d'EmuleProfil pour l'utilisateur:", data.userId);

      // Si les données sont déjà complètes, utilisons-les directement 
      // (cas des notifications et de la transmission depuis onlinelateralusers)
      if (data.avatar !== undefined && data.occupation !== undefined) {
        console.log("Utilisation des données utilisateur complètes pour EmuleProfil:", data);
        setEmuleUser(data);
        setShowEmuleProfil(true);
        return;
      }

      // Sinon, récupérer les données depuis Firestore
      try {
        // Récupérer les données complètes de l'utilisateur depuis Firestore
        const userDocRef = doc(db, 'users', data.userId);
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists()) {
          const userData = userDoc.data();
          console.log("Données utilisateur récupérées pour EmuleProfil:", userData);
          // Combiner les données de Firestore avec les données passées en paramètre
          setEmuleUser({
            ...data,
            ...userData,
            userId: data.userId, // Assurer que l'ID est bien celui passé en paramètre
            uid: data.userId     // S'assurer que uid est également présent
          });
          setShowEmuleProfil(true);
        } else {
          console.error("Aucune donnée utilisateur trouvée pour:", data.userId);
          // Si pas de données, on utilise quand même ce qu'on a
          setEmuleUser(data);
          setShowEmuleProfil(true);
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des données utilisateur:", error);
        // En cas d'erreur, on utilise les données disponibles
        setEmuleUser(data);
        setShowEmuleProfil(true);
      }
    }
  };

  // Fonction pour ouvrir la fenêtre de chat
  const openChatWindow = async (data) => {
    // Vérifier si l'événement n'est pas trop rapproché d'un précédent
    const now = new Date().getTime();
    if (now - lastRequestTimeRef.current < 500) return;

    lastRequestTimeRef.current = now;

    if (data && data.userId) {
      console.log("Ouverture de ChatWindow pour l'utilisateur:", data.userId);

      try {
        // Récupérer les données complètes de l'utilisateur depuis Firestore
        const userDocRef = doc(db, 'users', data.userId);
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists()) {
          const userData = userDoc.data();
          console.log("Données utilisateur récupérées pour ChatWindow:", userData);

          // Préparer les données pour le chat en combinant les données Firestore et les props
          setChatPeer({
            uid: data.userId,
            username: userData.username || data.username || "Utilisateur",
            avatarUrl: userData.avatar || data.avatar || null,
            avatar: userData.avatar || data.avatar || null
          });
          setShowChatWindow(true);
        } else {
          console.error("Aucune donnée utilisateur trouvée pour:", data.userId);
          // Si pas de données, on utilise quand même ce qu'on a
          setChatPeer({
            uid: data.userId,
            username: data.username || "Utilisateur",
            avatarUrl: data.avatar || null,
            avatar: data.avatar || null
          });
          setShowChatWindow(true);
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des données utilisateur:", error);
        // En cas d'erreur, on utilise les données disponibles
        setChatPeer({
          uid: data.userId,
          username: data.username || "Utilisateur",
          avatarUrl: data.avatar || null,
          avatar: data.avatar || null
        });
        setShowChatWindow(true);
      }
    }
  };

  useEffect(() => {
    // Écouteur pour l'événement d'ouverture d'EmuleProfil
    const handleEmuleProfilEvent = (event) => {
      openEmuleProfil(event.detail);
    };

    // Écouteur pour l'événement d'ouverture de ChatWindow
    const handleChatWindowEvent = (event) => {
      openChatWindow(event.detail);
    };

    // Ajouter les écouteurs d'événements
    window.addEventListener('openEmuleProfil', handleEmuleProfilEvent);
    window.addEventListener('openChatWindow', handleChatWindowEvent);

    // Vérifier si on doit ouvrir des composants à partir du localStorage (venant d'une navigation)
    const checkLocalStorage = () => {
      // Vérifier si nous devons afficher EmuleProfil (venant d'une notification)
      const emuleProfilData = localStorage.getItem('showEmuleProfil');
      if (emuleProfilData) {
        try {
          const userData = JSON.parse(emuleProfilData);
          openEmuleProfil(userData);
          // Nettoyer après utilisation
          localStorage.removeItem('showEmuleProfil');
        } catch (error) {
          console.error("Erreur lors du traitement des données EmuleProfil:", error);
        }
      }

      // Vérifier si nous devons afficher ChatWindow (venant d'une notification)
      const chatData = localStorage.getItem('openChatWith');
      if (chatData) {
        try {
          const peerData = JSON.parse(chatData);
          if (peerData.forceOpen) {
            openChatWindow(peerData);
            // Nettoyer après utilisation seulement si forceOpen est présent
            localStorage.removeItem('openChatWith');
          }
        } catch (error) {
          console.error("Erreur lors du traitement des données de chat:", error);
        }
      }
    };

    // Vérifier immédiatement après le montage du composant
    checkLocalStorage();

    // Nettoyer lors du démontage du composant
    return () => {
      window.removeEventListener('openEmuleProfil', handleEmuleProfilEvent);
      window.removeEventListener('openChatWindow', handleChatWindowEvent);
    };
  }, []);

  useEffect(() => {
    const stateTarget = location.state?.scrollTo;
    let storageTarget = null;

    if (typeof window !== 'undefined' && window.sessionStorage) {
      try {
        storageTarget = window.sessionStorage.getItem('mf-scroll-target');
      } catch {
        storageTarget = null;
      }
    }

    const target = stateTarget || storageTarget;

    if (target) {
      scrollToSection(target);
    }

    if (stateTarget) {
      history(location.pathname, { replace: true, state: {} });
    }
  }, [history, location, scrollToSection]);

  const handleActeurClick = (actorId) => {
    // Rediriger vers Acteurdetail.js avec l'ID de l'acteur
    history(`/acteurDetails/${actorId}`);
  };

  const handleCloseEmuleProfil = () => {
    setShowEmuleProfil(false);
    setEmuleUser(null);
  };

  const handleCloseChatWindow = () => {
    setShowChatWindow(false);
    setChatPeer(null);
  };

  const contextValue = {
    handleActeurClick: handleActeurClick,
  };

  return (
    <ActeurContext.Provider value={contextValue}>
      <>
        <Helmet>
          <title>Accueil - MovieFrenzy</title>
          <link rel="canonical" href="https://moviefrenzy.ca/" />
          <meta
            name="description"
            content="Bienvenue sur MovieFrenzy ! Découvrez notre plateforme dédiée aux films, acteurs et dernières nouveautés du cinéma."
          />
        </Helmet>
        <div
          style={{
            backgroundImage: `url(${process.env.PUBLIC_URL}/images/darkwater.jpg)`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            backgroundAttachment: "fixed",
            backgroundBlendMode: "overlay",
            backgroundColor: "#200211",
            opacity: "0.9",
            overflowX: "hidden",
            overflowY: "auto",
          }}
        >
          <NavigationBar />

          <div className="mf-scroll-slider">
            <Slider />
          </div>

          {/* Laboratoire : transition cinématographique */}
          <div
            ref={cinematicBridgeRef}
            className="mf-cinematic-bridge"
            aria-hidden="true"
          >
            <div className="mf-cinematic-bridge__light" />
          </div>

          <div className="mf-scroll-sections">
            <SectionsAccueil />
            <BackToTopButton />
          </div>
          <div>
            <Footer />
          </div>
          {/* Afficher EmuleProfil avec un Modal si nécessaire */}
          <Modal
            show={showEmuleProfil && emuleUser}
            onHide={handleCloseEmuleProfil}
            className="modalProfil"
            style={{ opacity: '100%', width: '100%', minHeight: '90%', height: '90%', marginTop: '2rem' }}
          >
            <Modal.Dialog
              id="custom-modal"
              style={{
                maxWidth: '70vw',
                height: '100%',
                maxHeight: '100%',
                backgroundColor: '#00FFFF',
                border: '3px solid #FFFFFF',
                boxShadow: '8px 4px 4px #00FFFF',
                overflowY: 'hidden',
              }}
            >
              <Modal.Header
                style={{
                  backgroundColor: '#000',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'stretch',
                }}
              >
                {/* Rangée 1 : Bouton de fermeture */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    marginBottom: '10px',
                  }}
                >
                  <button
                    onClick={handleCloseEmuleProfil}
                    style={{
                      width: '50px',
                      height: '50px',
                      background: '#000',
                      border: 'none',
                      color: '#FF33CC',
                      fontSize: '1.5rem',
                      cursor: 'pointer',
                      outline: 'none',
                      borderRadius: '50%',
                      transition: 'all 0.3s ease',
                    }}
                  >
                    ✕
                  </button>
                </div>

                {/* Rangée 2 : Username et occupation */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    width: '100%',
                  }}
                >
                  <Modal.Title style={{ color: '#D90093', textAlign: 'left', fontSize: 'clamp(1.25em, 2vw, 1.5em)' }}>
                    {emuleUser ? emuleUser.username : ''}
                  </Modal.Title>
                  <Modal.Title style={{ color: '#00FFFF', textAlign: 'right', fontSize: 'clamp(1.25em, 2vw, 1.5em)' }}>
                    {emuleUser ? emuleUser.occupation : ''}
                  </Modal.Title>
                </div>
              </Modal.Header>
              <Modal.Body style={{ backgroundColor: '#200211' }}>
                <EmuleProfil selectedUser={emuleUser} onClose={handleCloseEmuleProfil} />
              </Modal.Body>
            </Modal.Dialog>
          </Modal>

          {/* Afficher ChatWindow si nécessaire */}
          {showChatWindow && chatPeer && userId && (
            <ChatWindow
              currentUserId={userId}
              peerUser={chatPeer}
              onClose={handleCloseChatWindow}
            />
          )}
        </div>
      </>
    </ActeurContext.Provider>
  );
};

export default Accueil;
